import React from 'react';

interface BpmAdjustControlProps {
  currentBpm: number;
  detectedBpm: number | undefined;
  onChange: (bpm: number) => void;
}

const PRESETS = [60, 80, 100, 120, 160] as const;
const MIN_BPM = 40;
const MAX_BPM = 240;

const BpmAdjustControl: React.FC<BpmAdjustControlProps> = ({
  currentBpm,
  detectedBpm,
  onChange,
}) => {
  const isCustom = detectedBpm !== undefined && currentBpm !== detectedBpm;
  const hasDetected = detectedBpm !== undefined;

  const clamp = (v: number) => Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(v)));

  return (
    <div role="group" aria-label="BPM調整" className="px-3 py-2">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-slate-200">BPM</span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => onChange(clamp(currentBpm - 1))}
          className="w-8 h-8 rounded-lg border border-slate-600 bg-slate-700 text-slate-300 hover:bg-slate-600"
          aria-label="BPMを1減らす"
        >
          −
        </button>
        <input
          type="number"
          min={MIN_BPM}
          max={MAX_BPM}
          value={currentBpm}
          onChange={(e) => onChange(clamp(parseInt(e.target.value, 10) || currentBpm))}
          className="w-20 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-center text-slate-200"
          aria-label="BPM"
        />
        <button
          onClick={() => onChange(clamp(currentBpm + 1))}
          className="w-8 h-8 rounded-lg border border-slate-600 bg-slate-700 text-slate-300 hover:bg-slate-600"
          aria-label="BPMを1増やす"
        >
          +
        </button>
      </div>

      <input
        type="range"
        min={MIN_BPM}
        max={MAX_BPM}
        value={currentBpm}
        onChange={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
        className="w-full mb-3"
        aria-label="BPM スライダー"
      />

      <div className="flex gap-1 mb-2">
        {PRESETS.map((p) => (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`flex-1 px-2 py-1 text-xs rounded border transition-colors ${
              currentBpm === p
                ? 'border-amber-500 bg-amber-950/50 text-amber-400'
                : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {p}
          </button>
        ))}
      </div>

      <div className="text-xs text-slate-500">
        {hasDetected && !isCustom && <span>楽譜から検出: {detectedBpm}</span>}
        {hasDetected && isCustom && <span>カスタム（検出値: {detectedBpm}）</span>}
        {!hasDetected && <span>楽譜にテンポ情報がなく、デフォルト 120 を使用</span>}
      </div>
    </div>
  );
};

export default BpmAdjustControl;
